import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';
import {
  pushNotificationService,
  PushSubscriptionData,
} from '../services/pushNotificationService';
import {
  SubscribeToPushRequest,
  SubscribeToPushResponse,
  UnsubscribeFromPushRequest,
  UnsubscribeFromPushResponse,
} from '@dreamai/types';

/**
 * Hook for managing Push API notifications with Service Worker
 * Provides methods to check, request, and monitor push notification status
 */
export const usePushNotification = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [permission, setPermission] = useState<PermissionState>('prompt');
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isFullySetup, setIsFullySetup] = useState<boolean>(false);
  const [subscriptionData, setSubscriptionData] =
    useState<PushSubscriptionData | null>(null);

  // VAPID public key from environment variable
  const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || null;

  // Initialize push notification service
  useEffect(() => {
    const initializeService = async () => {
      console.log('🚀 Initializing push notification service...');

      const supported = pushNotificationService.isSupported();
      setIsSupported(supported);

      if (supported) {
        const initialized = await pushNotificationService.initialize();
        setIsInitialized(initialized);

        if (initialized) {
          const permissionStatus =
            await pushNotificationService.getPermissionStatus();
          setPermission(permissionStatus);
          setIsSubscribed(pushNotificationService.isSubscribed);

          // Check if we're fully set up (permission + active subscription)
          const fullySetup = await pushNotificationService.isFullySetup();
          setIsFullySetup(fullySetup);

          if (permissionStatus === 'granted' && vapidPublicKey) {
            try {
              // Ensure a browser-level push subscription exists
              const subscription =
                await pushNotificationService.subscribeToPush(vapidPublicKey);
              if (subscription) {
                setSubscriptionData(subscription);
                setIsSubscribed(true);
                setIsFullySetup(true);

                // Always sync to backend — the backend upserts by endpoint so
                // duplicates are harmless. This ensures the backend always has
                // the subscription even if a previous fire-and-forget failed.
                sendSubscriptionToBackend(subscription).catch((err) =>
                  console.warn('⚠️ Background subscription sync failed:', err),
                );
                console.log('📤 Syncing push subscription to backend');
              }
            } catch (resubErr) {
              console.warn('⚠️ Auto-resubscribe failed:', resubErr);
            }
          }
        }
      }
    };

    initializeService();
  }, []);

  // Request notification permission and subscribe to push
  const requestPermissionAndSubscribe =
    useCallback(async (): Promise<boolean> => {
      if (!isSupported || !isInitialized) {
        console.warn('❌ Push notifications not supported or not initialized');
        return false;
      }

      // Check if we're already fully set up (permission + active subscription)
      const fullySetup = await pushNotificationService.isFullySetup();
      if (fullySetup) {
        console.log('✅ Already fully set up for push notifications');
        return true;
      }

      if (permission === 'denied') {
        console.warn('❌ Notification permission previously denied');
        return false;
      }

      setIsRequesting(true);

      try {
        // Request permission first
        const granted = await pushNotificationService.requestPermission();
        const permissionStatus =
          await pushNotificationService.getPermissionStatus();
        setPermission(permissionStatus);

        if (!granted) {
          console.warn('❌ Permission not granted');
          return false;
        }

        // Subscribe to push notifications
        if (vapidPublicKey) {
          const subscription =
            await pushNotificationService.subscribeToPush(vapidPublicKey);

          if (subscription) {
            setSubscriptionData(subscription);
            setIsSubscribed(true);

            // Update fully setup status
            const fullySetup = await pushNotificationService.isFullySetup();
            setIsFullySetup(fullySetup);

            console.log('✅ Successfully subscribed to push notifications');

            // Send subscription to backend in the background — don't block
            // the UI (getAccessTokenSilently can hang on iOS PWA).
            sendSubscriptionToBackend(subscription).catch((err) =>
              console.error('⚠️ Background subscription sync failed:', err),
            );

            return true;
          } else {
            console.error('❌ Failed to create push subscription');
            return false;
          }
        } else {
          console.error('❌ VAPID public key not configured');
          return false;
        }
      } catch (error) {
        console.error('💥 Error requesting permission and subscribing:', error);
        return false;
      } finally {
        setIsRequesting(false);
      }
    }, [
      isSupported,
      isInitialized,
      permission,
      isSubscribed,
      vapidPublicKey,
      getAccessTokenSilently,
    ]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!isSubscribed) {
      console.log('ℹ️ Not currently subscribed');
      return true;
    }

    try {
      const success = await pushNotificationService.unsubscribeFromPush();

      if (success) {
        setIsSubscribed(false);
        setIsFullySetup(false);
        setSubscriptionData(null);

        await removeSubscriptionFromBackend();

        console.log('✅ Successfully unsubscribed');
      }

      return success;
    } catch (error) {
      console.error('💥 Error unsubscribing:', error);
      return false;
    }
  }, [isSubscribed]);

  // Send subscription data to backend
  const sendSubscriptionToBackend = async (
    subscription: PushSubscriptionData,
  ): Promise<void> => {
    try {
      console.log('📤 Sending subscription to backend...');

      // Get Auth0 token for authenticated API call
      const token = await getAccessTokenSilently();

      const requestData: SubscribeToPushRequest = {
        subscription,
        userAgent: navigator.userAgent,
      };

      const response = await api.post<SubscribeToPushResponse>(
        '/push/subscribe',
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        console.log(
          '✅ Subscription sent to backend successfully:',
          response.data.subscriptionId,
        );
      } else {
        console.error(
          '❌ Failed to send subscription to backend:',
          response.data.error,
        );
      }
    } catch (error: any) {
      console.error(
        '💥 Error sending subscription to backend:',
        error.response?.data?.error || error.message,
      );
    }
  };

  // Remove subscription from backend
  const removeSubscriptionFromBackend = async (): Promise<void> => {
    if (!subscriptionData) {
      console.warn('⚠️ No subscription data available for removal');
      return;
    }

    try {
      console.log('📤 Removing subscription from backend...');

      // Get Auth0 token for authenticated API call
      const token = await getAccessTokenSilently();

      const requestData: UnsubscribeFromPushRequest = {
        endpoint: subscriptionData.endpoint,
      };

      const response = await api.post<UnsubscribeFromPushResponse>(
        '/push/unsubscribe',
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        console.log('✅ Subscription removed from backend successfully');
      } else {
        console.error(
          '❌ Failed to remove subscription from backend:',
          response.data.error,
        );
      }
    } catch (error: any) {
      console.error(
        '💥 Error removing subscription from backend:',
        error.response?.data?.error || error.message,
      );
    }
  };

  // Check if permission is granted
  const isGranted = permission === 'granted';

  // Check if permission is denied
  const isDenied = permission === 'denied';

  // Check if permission is prompt (not yet requested)
  const isDefault = permission === 'prompt';

  return {
    // Permission state
    permission,
    isSupported,
    isGranted,
    isDenied,
    isDefault,

    // Service state
    isInitialized,
    isSubscribed,
    isFullySetup,
    isRequesting,
    subscriptionData,

    // Actions
    requestPermissionAndSubscribe,
    unsubscribe,
  };
};
